/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { getUserId } from "Models/User";
import { Button, Input, Progress, Stack } from "components";
import useCompanion from "hooks/useCompanion";
import useRouting from "hooks/useRouting";
import useTryCatch from "hooks/useTryCatch";
import { updatePreferences } from "lib/preferencesApi";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useUser } from "state/UserContext";
import { PageLayout } from "templates";
import { useOrgData } from "state/OrgDataContext";
import { usePrefNavigator } from "state/PrefNavigatorContext";
import usePreviousRoute from "utils/usePreviousRoute";
import { useEnvs } from "state/EnvironmentsContext/EnvContext";
import { useTranslation } from "react-i18next";

import { pushToDataLayer } from "utils/gtm";

const Notes = () => {
  const { t } = useTranslation();
  const { isLoading, setLoading, userData } = useUser();
  const { shouldEnterDonePage } = useRouting();
  const { dataContent: EVENT_CONTENT } = useOrgData();

  const { userId } = useParams();
  const [companion, setCompanion] = useCompanion(userId);
  const { navigateWithEvent, eventId } = useEventRouting();
  const [note, setNote] = useState("");
  const [specialAssistance, setSpecialAssistance] = useState("");
  const goBack = usePreviousRoute();

  const { progressBarValues, getPreviousStepName, setCurrentStep } =
    usePrefNavigator();
  useEffect(() => {
    setCurrentStep("notes");
  }, [setCurrentStep]);

  const onGoBack = () => {
    pushToDataLayer({
      event: "preferences_back",
      screen_name: "special notes",
      journey_step: "notes",
    });
    const previousStepName = getPreviousStepName();
    if (previousStepName == null) {
      navigateWithEvent(ROUTES.choices.base(userId));
    }
    if (previousStepName) {
      goBack();
    }
  };

  const handleOnSubmit = useTryCatch(async (e) => {
    e.preventDefault();
    pushToDataLayer({
      event: "next_notes",
      screen_name: "special notes",
      journey_step: "notes",
    });
    setLoading(true);
    const id = getUserId(companion) || userId;
    await updatePreferences(
      id,
      {
        isCompanion: id !== getUserId(userData),
        other_notes: note,
        special_medical_assistance: specialAssistance,
      },
      eventId,
    );
    setLoading(false);

    const newCompanions = setCompanion({
      ...companion,
      other_notes: note,
    });

    const goTo = await shouldEnterDonePage(newCompanions);
    navigateWithEvent(goTo);
  }, setLoading);

  useEffect(() => {
    if (!companion?.other_notes) return;
    setNote(companion.other_notes);
    setSpecialAssistance(companion.special_medical_assistance);
  }, [companion]);

  const formRef = useRef(null);
  const { event: EVENT } = useEnvs();
  return (
    <PageLayout
      title={t("notes.title")}
      note={
        EVENT === "SPORTICO"
          ? EVENT_CONTENT.notes.description
          : t("notes.description")
      }
      hasPolicy={false}
      hasGoBack
      goBackMethod={onGoBack}
      hasProgressBar
      progressBarValues={progressBarValues}
    >
      <Stack component="article" className="mt-10 max-w-[400px] mx-auto">
        <form
          ref={formRef}
          onSubmit={handleOnSubmit}
          className="justify-between sm:justify-center min-h-full"
        >
          <Stack className="gap-y-10">
            <Input
              placeholder={t(
                "medicalConditions.specialAssistanceNeededPlaceholder",
              )}
              label={t("medicalConditions.specialAssistanceLabel")}
              type="textarea"
              rows={3}
              value={specialAssistance}
              onChange={(e) => {
                setSpecialAssistance(e.target.value);
              }}
            />
            <Input
              placeholder={t("notes.commentsPlaceholder")}
              id="note"
              name="note"
              label={t("notes.commentsLabel")}
              type="textarea"
              rows={4}
              value={note}
              onChange={({ target: { value } }) => setNote(value)}
            />
          </Stack>
          <Stack className="mt-8">
            <div className="flex sm:hidden gap-x-0.5">
              {progressBarValues.map((value, index) => (
                <Progress key={index} value={value} />
              ))}
            </div>
            <div className="mt-2 w-full">
              <Button
                variant="primary"
                onClick={(e) => handleOnSubmit(e)}
                isLoading={isLoading}
                fullWidth
              >
                {t("notes.continueButtonLabel")}
              </Button>
            </div>
          </Stack>
        </form>
      </Stack>
    </PageLayout>
  );
};
export default Notes;
